import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent } from "../utils/permissions";
import { libelleSource } from "../lib/sourceTranscription";
import { Button } from "../components/ui/button";
import {
  CaretDown, CaretRight, FilmStrip, Sparkle, Trash, Pencil, FloppyDisk,
  Check, X, ChatCircleText, Lightbulb, Shield, ClockCounterClockwise, Clock, Heart,
  CheckCircle, FileText,
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

/**
 * Sales Scripts page (Vincent V 2026-05-01).
 * Shows ALL scripts in the current org, grouped by source video.
 * Each script renders V1/V2/V3 + validation table with V2 expanded by default.
 */
export default function ScriptsPage({ embedded = false, readOnly = false, onSeek = null, onCardClick = null, onScriptsListChange = null, reloadTick = 0 } = {}) {
  const { videoId } = useParams();   // optional — if present, filter by video
  const { user } = useAuth();
  const { lang, tr } = useLang();
  const navigate = useNavigate();
  // Vincent B858 (Eric 2026-02-14 SPEC STRICK PROD) — Deep-link
  // `?focus={script_id}` : le chatbot peut citer un script depuis
  // l'inventaire B857 avec un lien markdown vers cette page. On
  // scrolle et surligne la carte 800ms pour attirer l'œil.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusScriptId = searchParams.get("focus");
  // Vincent #370 (Eric 2026-06-22 SPEC STRICK) → Eric 2026-06-25 (iter118 B3)
  // — migré vers canEditContent (helper canonique aligné backend require_editor).
  // Concepteur a les MÊMES droits d'édition des Scripts que founder/admin.
  const isAdminRaw = canEditContent(user);
  // Vincent bug #108 (2026-05-08) — Onglet "Visualiser" en lecture seule : on désactive
  // les affordances admin (édition titre, approve/reject, supprimer) pour ne montrer que la lecture.
  const isAdmin = isAdminRaw && !readOnly;

  // Vincent #99 (2026-05-12) — Parse [HH:MM:SS] ou [MM:SS] → secondes pour click→seek.
  const parseTimeCodeToSeconds = (tc) => {
    if (!tc) return null;
    const m = String(tc).match(/\[?(\d{1,2}):(\d{2})(?::(\d{2}))?\]?/);
    if (!m) return null;
    const h = m[3] ? parseInt(m[1], 10) : 0;
    const min = m[3] ? parseInt(m[2], 10) : parseInt(m[1], 10);
    const sec = m[3] ? parseInt(m[3], 10) : parseInt(m[2], 10);
    return h * 3600 + min * 60 + sec;
  };
  const seekFromTimeCode = (tc, scriptVideoId = null) => {
    const s = parseTimeCodeToSeconds(tc);
    if (s === null) return;
    if (typeof onSeek === "function") {
      onSeek(s);
      return;
    }
    // Standalone : naviguer vers /dashboard/videos/{id}?t={s}
    const vid = videoId || scriptVideoId;
    if (vid) navigate(`/dashboard/videos/${vid}?t=${s}`);
  };

  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Vincent B546 (Eric 2026-07-27) — Système de favoris pour Scripts de vente.
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  // Vincent XLSX 2026-07-16 Row 498 (Eric 2026-07-16 SPEC STRICK) —
  // Ajout de filtres UI : par vidéo source + par statut (en révision, approuvé, etc).
  // Les états sont locaux (non persistés) — remis à zéro à chaque visite.
  const [filterVideoId, setFilterVideoId] = useState("all"); // "all" | video_id
  const [filterStatus, setFilterStatus] = useState("all"); // "all" | "pending_review" | "approved" | "rejected"
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null); // {phase, current, total, currentTitle, created, skipped, candidates}
  const [openIds, setOpenIds] = useState({});         // {scriptId: true}
  const [v3Open, setV3Open] = useState({});           // {scriptId: true} — V3 collapsed by default
  const [fullScripts, setFullScripts] = useState({}); // {scriptId: fullDoc} cache
  const [deletingId, setDeletingId] = useState(null);
  // Vincent bug #62 (2026-05-07) — inline title editing.
  const [editingScriptId, setEditingScriptId] = useState(null);
  // Vincent #362 (Eric 2026-06-19 SPEC STRICK) — Modal historique scripts
  const [historyEntry, setHistoryEntry] = useState(null);
  // Vincent B794 v2 (Eric 2026-02-12) — État du bouton « Régénérer
  // validation » (id du script en cours de régénération, ou null).
  const [regeneratingValidationFor, setRegeneratingValidationFor] = useState(null);
  const [scriptTitleDraft, setScriptTitleDraft] = useState("");
  // Vincent bug #79 (2026-05-07) — title regeneration proposals + history.
  const [titleProposalsLoading, setTitleProposalsLoading] = useState(null);
  const [titleProposals, setTitleProposals] = useState(null); // {scriptId, proposals, history}
  // Vincent (Eric 2026-05-18) — Édition inline du CONTENU des scripts (V2 / V3).
  // Le user veut pouvoir corriger le texte des tours de parole reformulés, pas
  // seulement le titre. On édite tour-par-tour : seul `text` est modifiable,
  // `role` et `time_code` restent inchangés pour préserver l'alignement Whisper.
  const [editingContent, setEditingContent] = useState(null); // {scriptId, version: 'v2'|'v3'}
  const [contentDraft, setContentDraft] = useState([]); // [{role, text, time_code, start_seconds}]
  const [savingContent, setSavingContent] = useState(false);
  // Scripts Vague 2 (Eric 2026-02-11) — Vue Corbeille + commentaires.
  const [viewMode, setViewMode] = useState("active"); // "active" | "trash"
  const [trashScripts, setTrashScripts] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [commentsByScript, setCommentsByScript] = useState({}); // {scriptId: [comment...]}
  const [commentsLoadingId, setCommentsLoadingId] = useState(null);
  const [commentDrafts, setCommentDrafts] = useState({}); // {scriptId: text} for top-level
  const [replyDrafts, setReplyDrafts] = useState({}); // {parent_id: text}
  const [replyingTo, setReplyingTo] = useState(null); // parent_id being replied to
  const [commentsOpenForId, setCommentsOpenForId] = useState({}); // {scriptId: true}
  const [submittingComment, setSubmittingComment] = useState(false);

  const startEditContent = (scriptDoc, version) => {
    const arr = (version === "v3" ? scriptDoc.v3_justifie : scriptDoc.v2_reformule) || [];
    setEditingContent({ scriptId: scriptDoc.id, version });
    setContentDraft(arr.map(t => ({ ...t })));
  };

  const cancelEditContent = () => {
    setEditingContent(null);
    setContentDraft([]);
  };

  const saveEditContent = async () => {
    if (!editingContent) return;
    setSavingContent(true);
    try {
      const field = editingContent.version === "v3" ? "v3_justifie" : "v2_reformule";
      await axios.patch(
        `${API}/api/scripts/${editingContent.scriptId}`,
        { [field]: contentDraft },
        { withCredentials: true }
      );
      // Refresh the full script cache + the lite list (status etc.).
      try {
        const { data: full } = await axios.get(
          `${API}/api/scripts/${editingContent.scriptId}`,
          { withCredentials: true }
        );
        setFullScripts(prev => ({ ...prev, [editingContent.scriptId]: full }));
      } catch { /* refresh silencieux : l'edit save ci-dessus est déjà confirmé, on ré-affichera au prochain load() */ }
      toast.success(lang === "fr" ? "Contenu sauvegardé" : "Content saved");
      setEditingContent(null);
      setContentDraft([]);
      await load();
    } catch (err) {
      toast.error(`Erreur : ${err.response?.data?.detail || err.message}`);
    } finally {
      setSavingContent(false);
    }
  };

  // Vincent B546 (Eric 2026-07-27) — Charger la liste des favoris Scripts.
  const loadFavorites = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/favorites`, { withCredentials: true });
      setFavoriteIds(new Set(data.filter(f => f.item_type === "sales_script").map(f => f.item_id)));
    } catch { /* favoris facultatifs : on tolère l'échec réseau pour ne pas casser l'affichage de la page scripts */ }
  }, []);

  // Vincent B546 (Eric 2026-07-27) — Toggle avec optimistic update + retry
  // safe + toast erreur (mêmes patterns que KnowledgePage post-fix).
  const toggleFavorite = async (e, scriptId) => {
    e.stopPropagation();
    const wasFavorite = favoriteIds.has(scriptId);
    setFavoriteIds(prev => {
      const s = new Set(prev);
      if (wasFavorite) s.delete(scriptId); else s.add(scriptId);
      return s;
    });
    try {
      if (wasFavorite) {
        await axios.delete(`${API}/api/favorites/sales_script/${scriptId}`, { withCredentials: true, _allowRetry: true });
      } else {
        await axios.post(`${API}/api/favorites`, { type: "sales_script", item_id: scriptId }, { withCredentials: true, _allowRetry: true });
      }
    } catch (err) {
      setFavoriteIds(prev => {
        const s = new Set(prev);
        if (wasFavorite) s.add(scriptId); else s.delete(scriptId);
        return s;
      });
      const msg = err?.response?.data?.detail || err?._friendlyMessage || "Impossible de mettre à jour le favori. Réessayez.";
      toast.error(msg);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = videoId
        ? `${API}/api/videos/${videoId}/scripts`
        : `${API}/api/scripts`;
      const { data } = await axios.get(url, { withCredentials: true });
      const list = data?.scripts || [];
      setScripts(list);
      // Eric (2026-02-10) — Expose la liste au parent pour permettre la
      // navigation Prev/Next dans le KnowledgeDetailModal partagé (Scripts).
      if (typeof onScriptsListChange === "function") {
        onScriptsListChange(list);
      }
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur de chargement des scripts" : "Failed to load scripts");
    } finally {
      setLoading(false);
    }
  }, [videoId, lang, onScriptsListChange]);

  // Vincent P1.9.c (Eric 2026-07-31 SPEC STRICK PROD) — useEffect déplacé
  // ici (après load + loadFavorites) pour permettre l'inclusion des 2
  // callbacks dans les deps sans TDZ.
  useEffect(() => { load(); loadFavorites(); }, [load, loadFavorites]);

  // Vincent B858 (Eric 2026-02-14 SPEC STRICK PROD) — Auto-scroll +
  // highlight de la carte ciblée par `?focus={script_id}` (deep-link
  // depuis le chatbot cf. inventaire B857). On attend que les scripts
  // soient chargés + un tick pour laisser le DOM se rendre.
  useEffect(() => {
    if (!focusScriptId || scripts.length === 0) return;
    const target = scripts.find((s) => s.id === focusScriptId);
    if (!target) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-focus-target="${focusScriptId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-4", "ring-amber-400", "ring-offset-2", "transition-shadow");
        setTimeout(() => {
          el.classList.remove("ring-4", "ring-amber-400", "ring-offset-2");
        }, 3000);
      }
      // Nettoie le query param `focus` uniquement (préserve `tab=script`
      // et autres params) pour ne pas ré-scroller au moindre changement
      // ET pour ne pas ré-initialiser l'onglet actif de KnowledgePage
      // (qui écoute `location.search` et repasserait sur l'onglet par
      // défaut si on lui envoyait un `{}`).
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }, 400);
    return () => clearTimeout(timer);
  }, [focusScriptId, scripts, setSearchParams]);
  // Eric (2026-02-10 — Vague 1 P2) — Re-fetch scripts après une édition
  // depuis la modale partagée (title / turns / status) via un tick
  // incrémental publié par VideoDetailPage. Skip 0 (mount initial).
  useEffect(() => {
    if (reloadTick > 0) { load(); }
  }, [reloadTick]);

  const expand = async (scriptId) => {
    // Eric (2026-02-10) — Filet Prev/Next : en mode embarqué avec un
    // `onCardClick` fourni, on ouvre la modale partagée (lecture seule)
    // au lieu du dépliage inline. Le concepteur continue d'éditer via
    // l'UI inline en cliquant sur « ✏️ Éditer » dans les boutons d'action.
    if (embedded && typeof onCardClick === "function") {
      let doc = fullScripts[scriptId];
      if (!doc) {
        try {
          const { data } = await axios.get(`${API}/api/scripts/${scriptId}`, { withCredentials: true });
          setFullScripts((p) => ({ ...p, [scriptId]: data }));
          doc = data;
        } catch (e) {
          toast.error(lang === "fr" ? "Erreur de chargement" : "Failed to load");
          return;
        }
      }
      onCardClick(doc);
      return;
    }
    if (openIds[scriptId]) {
      setOpenIds((p) => ({ ...p, [scriptId]: false }));
      return;
    }
    setOpenIds((p) => ({ ...p, [scriptId]: true }));
    if (!fullScripts[scriptId]) {
      try {
        const { data } = await axios.get(`${API}/api/scripts/${scriptId}`, { withCredentials: true });
        setFullScripts((p) => ({ ...p, [scriptId]: data }));
      } catch (e) {
        toast.error(lang === "fr" ? "Erreur de chargement" : "Failed to load");
      }
    }
  };

  const generateForVideo = async () => {
    if (!videoId) return;
    const ok = await confirmDialog(
      lang === "fr"
        ? "Détecter et générer les scripts de vente pour cette vidéo ? Le pipeline IA peut prendre 1-3 minutes."
        : "Detect and generate sales scripts for this video? AI pipeline takes 1-3 minutes.",
      { confirmLabel: lang === "fr" ? "Générer" : "Generate" },
    );
    if (!ok) return;
    setGenerating(true);
    setProgress({ phase: "starting", current: 0, total: 0, created: 0, skipped: 0, candidates: [], currentTitle: "" });

    try {
      // Use fetch + ReadableStream for SSE-style streaming.
      // Vincent P1.10 (Eric 2026-07-31 SPEC STRICK PROD) — credentials:"include"
      // envoie automatiquement le cookie httpOnly ; plus de Bearer localStorage.
      const resp = await fetch(`${API}/api/videos/${videoId}/generate-scripts-stream`, {
        method: "GET",
        credentials: "include",
      });

      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => "");
        throw new Error(text || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let finalSummary = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE frames separated by \n\n
        let sepIdx;
        while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          const lines = frame.split("\n").filter((l) => l.startsWith("data:"));
          if (!lines.length) continue;
          const payload = lines.map((l) => l.slice(5).trim()).join("\n");
          let evt;
          try { evt = JSON.parse(payload); } catch { continue; }

          if (evt.event === "start") {
            setProgress((p) => ({ ...p, phase: "detecting" }));
          } else if (evt.event === "detection_done") {
            setProgress((p) => ({ ...p, phase: "detected", total: evt.total_detected || 0, candidates: evt.candidates || [] }));
          } else if (evt.event === "script_start") {
            setProgress((p) => ({ ...p, phase: "generating", current: evt.index, total: evt.total, currentTitle: evt.title }));
          } else if (evt.event === "script_done") {
            setProgress((p) => ({ ...p, created: (p?.created || 0) + 1 }));
          } else if (evt.event === "script_skipped") {
            setProgress((p) => ({ ...p, skipped: (p?.skipped || 0) + 1 }));
          } else if (evt.event === "complete") {
            finalSummary = evt.summary;
            setProgress((p) => ({ ...p, phase: "complete" }));
          } else if (evt.event === "error") {
            throw new Error(evt.message || "Erreur pipeline");
          }
        }
      }

      if (finalSummary) {
        const created = finalSummary.scripts_created || 0;
        const skipped = finalSummary.scripts_skipped || 0;
        // Vincent bug Scripts empty state (2026-05-07) — feedback explicite
        // quand 0 script generé. Évite la confusion « rien ne s'est passé ».
        if (created === 0) {
          toast.warning(lang === "fr"
            ? `⚠️ Aucun script de vente détecté dans cette vidéo. ${skipped > 0 ? `${skipped} candidat(s) ignoré(s) (déjà créés ou non-pertinents).` : "Le pipeline n'a pas trouvé de pattern de script valide dans la transcription."}`
            : `⚠️ No sales script detected in this video. ${skipped > 0 ? `${skipped} candidate(s) skipped.` : "No valid script pattern found in the transcript."}`,
            { duration: 12000 });
        } else {
          toast.success(lang === "fr"
            ? `${created} script(s) généré(s) — ${skipped} ignore(s)`
            : `${created} script(s) generated — ${skipped} skipped`);
        }
      }
      load();
    } catch (e) {
      toast.error(lang === "fr"
        ? `Erreur : ${e.message}`
        : `Error: ${e.message}`);
    } finally {
      setGenerating(false);
      // Keep progress visible briefly so users see the final state
      setTimeout(() => setProgress(null), 4000);
    }
  };

  const deleteScript = async (scriptId) => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Envoyer ce script dans la corbeille ? Vous pourrez le restaurer plus tard."
        : "Move this script to trash? You can restore it later.",
      { danger: true, confirmLabel: lang === "fr" ? "Envoyer à la corbeille" : "Move to trash" },
    );
    if (!ok) return;
    setDeletingId(scriptId);
    try {
      await axios.delete(`${API}/api/scripts/${scriptId}`, { withCredentials: true });
      toast.success(lang === "fr" ? "Script envoyé à la corbeille" : "Script moved to trash");
      load();
    } catch (e) {
      toast.error(lang === "fr" ? "Suppression echouee" : "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  // Vincent B794 v2 (Eric 2026-02-12) — Régénère UNIQUEMENT le
  // tableau `validation_table` d'un script existant, via l'endpoint
  // dédié POST /api/agent/knowledge-units/{id}/regenerate-validation.
  const regenerateValidation = async (scriptId) => {
    if (regeneratingValidationFor) return;
    setRegeneratingValidationFor(scriptId);
    try {
      const { data } = await axios.post(
        `${API}/api/agent/knowledge-units/${scriptId}/regenerate-validation`,
        {},
        { withCredentials: true }
      );
      toast.success(
        lang === "fr"
          ? `Tableau régénéré (${data.rows} ligne${data.rows !== 1 ? "s" : ""})`
          : `Table regenerated (${data.rows} row${data.rows !== 1 ? "s" : ""})`
      );
      // Rafraîchit le cache local du script avec les nouvelles données.
      setFullScripts((prev) => {
        const cur = prev[scriptId];
        if (!cur) return prev;
        return {
          ...prev,
          [scriptId]: {
            ...cur,
            validation_table: data.validation_table || [],
          },
        };
      });
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          (lang === "fr" ? "Échec de la régénération" : "Regeneration failed")
      );
    } finally {
      setRegeneratingValidationFor(null);
    }
  };

  // Vincent B801 (Eric 2026-02-12) — Bulk regenerate validation.
  const [bulkRegenBusy, setBulkRegenBusy] = useState(false);
  const bulkRegenerateValidation = async (opts = {}) => {
    if (bulkRegenBusy) return;
    // scripts partagent le même video_id (embedded dans une vidéo)
    const videoId = (scripts[0] && scripts[0].video_id) || null;
    if (!videoId) {
      toast.error(lang === "fr" ? "Aucune vidéo détectée" : "No video detected");
      return;
    }
    // Vincent B812 (Eric 2026-02-13) — Support d'un mode "force" qui
    // régénère TOUS les scripts (utile quand l'auto-detect ne voit rien
    // à régénérer mais Vincent veut quand même refaire).
    const force = !!opts.force;
    const ok = await confirmDialog({
      title: force
        ? (lang === "fr" ? "FORCER la régénération de TOUS les scripts ?" : "FORCE regenerate ALL scripts?")
        : (lang === "fr" ? "Refaire la validation en lot ?" : "Bulk redo validation?"),
      description: force
        ? (lang === "fr"
            ? "Cette action FORCE la régénération de TOUS les scripts, même ceux considérés complets. Cela peut prendre 3-5 minutes et coûter en crédits BYOK/Emergent."
            : "This FORCES regeneration of ALL scripts, even those considered complete. May take 3-5 min and cost BYOK/Emergent credits.")
        : (lang === "fr"
            ? "Cette action refait la validation de TOUS les scripts de cette vidéo dont le tableau ou les justifications V3 sont vides ou incomplets. Cela peut prendre 1-3 minutes."
            : "This redoes validation for ALL scripts with empty/incomplete tables OR empty V3 justifications. May take 1-3 minutes."),
      confirmLabel: force
        ? (lang === "fr" ? "Forcer" : "Force")
        : (lang === "fr" ? "Refaire" : "Redo"),
      danger: force,
    });
    if (!ok) return;
    setBulkRegenBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/agent/videos/${videoId}/scripts/bulk-regenerate-validation${force ? "?force=true" : ""}`,
        {},
        { withCredentials: true }
      );
      // Vincent B812 : message plus explicite quand 0/0 (aucun candidat)
      const total = data.total_scripts ?? 0;
      const targeted = data.targeted ?? 0;
      const regenerated = data.regenerated?.length ?? 0;
      const failed = data.failed?.length ?? 0;
      if (total > 0 && targeted === 0) {
        toast.success(lang === "fr"
          ? `${total} script(s) analysés — aucun ne nécessite de régénération. Utilise « Forcer tout régénérer » si tu veux quand même refaire.`
          : `${total} script(s) analyzed — none need regeneration. Use "Force regen all" if you want to redo anyway.`);
      } else {
        toast.success(lang === "fr"
          ? `${regenerated}/${targeted} scripts régénérés (${failed} échec${failed !== 1 ? "s" : ""}) sur ${total} script(s) au total`
          : `${regenerated}/${targeted} scripts regenerated (${failed} failure${failed !== 1 ? "s" : ""}) of ${total} total`);
      }
      // Force reload of full script data
      setFullScripts({});
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec du bulk regenerate" : "Bulk regenerate failed"));
    } finally {
      setBulkRegenBusy(false);
    }
  };



  // Scripts Vague 2 (Eric 2026-02-11) — Restauration + suppression définitive.
  const loadTrash = useCallback(async () => {
    setTrashLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/scripts/trash`, { withCredentials: true });
      setTrashScripts(data?.scripts || []);
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur de chargement de la corbeille" : "Failed to load trash");
    } finally {
      setTrashLoading(false);
    }
  }, [lang]);

  const restoreScript = async (scriptId) => {
    try {
      await axios.post(`${API}/api/scripts/${scriptId}/restore`, {}, { withCredentials: true });
      toast.success(lang === "fr" ? "Script restauré" : "Script restored");
      await loadTrash();
      await load();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  const permanentDeleteScript = async (scriptId) => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Supprimer DÉFINITIVEMENT ce script ? Cette action est irréversible."
        : "PERMANENTLY delete this script? This cannot be undone.",
      { danger: true, confirmLabel: lang === "fr" ? "Supprimer définitivement" : "Delete permanently" },
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/scripts/${scriptId}?hard=true`, { withCredentials: true });
      toast.success(lang === "fr" ? "Script supprimé définitivement" : "Script permanently deleted");
      await loadTrash();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  useEffect(() => {
    if (viewMode === "trash") loadTrash();
  }, [viewMode, loadTrash]);

  // --- Comments (threaded, all org users can comment) ---
  const loadComments = useCallback(async (scriptId) => {
    setCommentsLoadingId(scriptId);
    try {
      const { data } = await axios.get(`${API}/api/scripts/${scriptId}/comments`, { withCredentials: true });
      setCommentsByScript(prev => ({ ...prev, [scriptId]: data?.comments || [] }));
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur de chargement des commentaires" : "Failed to load comments");
    } finally {
      setCommentsLoadingId(null);
    }
  }, [lang]);

  const toggleComments = async (scriptId) => {
    const isOpen = commentsOpenForId[scriptId];
    setCommentsOpenForId(prev => ({ ...prev, [scriptId]: !isOpen }));
    if (!isOpen && !commentsByScript[scriptId]) {
      await loadComments(scriptId);
    }
  };

  const submitComment = async (scriptId, parentId = null) => {
    const text = (parentId ? replyDrafts[parentId] : commentDrafts[scriptId] || "").trim();
    if (!text) return;
    setSubmittingComment(true);
    try {
      await axios.post(
        `${API}/api/scripts/${scriptId}/comments`,
        { text, parent_id: parentId },
        { withCredentials: true },
      );
      if (parentId) {
        setReplyDrafts(prev => ({ ...prev, [parentId]: "" }));
        setReplyingTo(null);
      } else {
        setCommentDrafts(prev => ({ ...prev, [scriptId]: "" }));
      }
      await loadComments(scriptId);
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    } finally {
      setSubmittingComment(false);
    }
  };

  const deleteComment = async (scriptId, commentId) => {
    const ok = await confirmDialog(
      lang === "fr" ? "Supprimer ce commentaire ?" : "Delete this comment?",
      { danger: true },
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/scripts/comments/${commentId}`, { withCredentials: true });
      await loadComments(scriptId);
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  // Build tree from flat comment list.
  const buildCommentTree = (comments) => {
    const byId = new Map(comments.map(c => [c.id, { ...c, children: [] }]));
    const roots = [];
    for (const c of byId.values()) {
      if (c.parent_id && byId.has(c.parent_id)) {
        byId.get(c.parent_id).children.push(c);
      } else {
        roots.push(c);
      }
    }
    return roots;
  };

  const renderCommentNode = (node, scriptId, depth = 0) => {
    const canDelete = isAdminRaw || (user && node.user_id === user.id);
    const isReplying = replyingTo === node.id;
    return (
      <div
        key={node.id}
        className={`py-2 ${depth > 0 ? "border-l-2 border-zinc-200 pl-3" : ""}`}
        style={{ marginLeft: depth > 0 ? Math.min(depth * 12, 48) : 0 }}
        data-testid={`comment-${node.id.slice(0, 8)}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-zinc-500 mb-0.5">
              <span className="font-semibold text-zinc-700">{node.user_name || node.user_email}</span>
              <span className="mx-1.5">·</span>
              <span>{new Date(node.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</span>
            </div>
            <p className="text-[12.5px] text-zinc-800 whitespace-pre-wrap leading-snug">{node.text}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {depth < 4 && (
              <button
                type="button"
                onClick={() => setReplyingTo(isReplying ? null : node.id)}
                className="text-[10.5px] px-1.5 py-0.5 rounded border border-zinc-200 bg-white hover:bg-zinc-100 text-zinc-600"
                data-testid={`reply-comment-${node.id.slice(0, 8)}`}
              >
                {lang === "fr" ? "Répondre" : "Reply"}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => deleteComment(scriptId, node.id)}
                className="text-[10.5px] px-1.5 py-0.5 rounded border border-red-200 bg-white hover:bg-red-50 text-red-600"
                data-testid={`delete-comment-${node.id.slice(0, 8)}`}
                title={lang === "fr" ? "Supprimer ce commentaire" : "Delete this comment"}
              >
                <Trash size={11} />
              </button>
            )}
          </div>
        </div>
        {isReplying && (
          <div className="mt-2 flex gap-1.5" data-testid={`reply-form-${node.id.slice(0, 8)}`}>
            <textarea
              value={replyDrafts[node.id] || ""}
              onChange={(e) => setReplyDrafts(prev => ({ ...prev, [node.id]: e.target.value }))}
              placeholder={lang === "fr" ? "Votre réponse…" : "Your reply…"}
              rows={2}
              className="flex-1 text-[12px] px-2 py-1 border border-zinc-300 rounded resize-y bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none"
              data-testid={`reply-input-${node.id.slice(0, 8)}`}
            />
            <div className="flex flex-col gap-1">
              <Button
                size="sm"
                onClick={() => submitComment(scriptId, node.id)}
                disabled={submittingComment || !(replyDrafts[node.id] || "").trim()}
                className="h-7 text-[11px] px-2 bg-[#002FA7] hover:bg-[#001F7A] text-white"
                data-testid={`submit-reply-${node.id.slice(0, 8)}`}
              >
                {submittingComment ? "…" : (lang === "fr" ? "Envoyer" : "Send")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReplyingTo(null)}
                className="h-6 text-[10px] px-2"
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </Button>
            </div>
          </div>
        )}
        {node.children && node.children.length > 0 && (
          <div className="mt-1">
            {node.children.map(child => renderCommentNode(child, scriptId, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderCommentsPanel = (scriptId) => {
    const open = commentsOpenForId[scriptId];
    const comments = commentsByScript[scriptId] || [];
    const loading = commentsLoadingId === scriptId;
    const draft = commentDrafts[scriptId] || "";
    const tree = buildCommentTree(comments);
    return (
      <details
        className="mt-2 bg-white rounded-lg border border-blue-200"
        open={!!open}
        onToggle={(e) => {
          const isOpen = e.currentTarget.open;
          if (isOpen && !commentsByScript[scriptId]) loadComments(scriptId);
          setCommentsOpenForId(prev => ({ ...prev, [scriptId]: isOpen }));
        }}
        data-testid={`comments-section-${scriptId.slice(0, 8)}`}
      >
        <summary className="px-3 py-2 cursor-pointer text-[12px] font-semibold text-blue-700 hover:bg-blue-50 flex items-center gap-2 list-none">
          <ChatCircleText size={14} weight="bold" />
          {lang === "fr" ? "Commentaires" : "Comments"}
          <span className="text-[10px] text-zinc-500 font-normal">({comments.length})</span>
        </summary>
        <div className="px-3 py-2 space-y-2">
          {loading && (
            <p className="text-[11px] text-zinc-500 italic">{lang === "fr" ? "Chargement…" : "Loading…"}</p>
          )}
          {!loading && comments.length === 0 && (
            <p className="text-[11px] text-zinc-500 italic" data-testid={`comments-empty-${scriptId.slice(0, 8)}`}>
              {lang === "fr" ? "Aucun commentaire — soyez le premier à démarrer la discussion." : "No comments yet — be the first to start the discussion."}
            </p>
          )}
          {tree.map(root => renderCommentNode(root, scriptId, 0))}
          <div className="pt-2 border-t border-zinc-100 flex gap-2">
            <textarea
              value={draft}
              onChange={(e) => setCommentDrafts(prev => ({ ...prev, [scriptId]: e.target.value }))}
              placeholder={lang === "fr" ? "Ajouter un commentaire…" : "Add a comment…"}
              rows={2}
              className="flex-1 text-[12px] px-2 py-1.5 border border-zinc-300 rounded resize-y bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none"
              data-testid={`new-comment-input-${scriptId.slice(0, 8)}`}
            />
            <Button
              size="sm"
              onClick={() => submitComment(scriptId)}
              disabled={submittingComment || !draft.trim()}
              className="bg-[#002FA7] hover:bg-[#001F7A] text-white self-start"
              data-testid={`submit-comment-${scriptId.slice(0, 8)}`}
            >
              {submittingComment ? "…" : (lang === "fr" ? "Publier" : "Post")}
            </Button>
          </div>
        </div>
      </details>
    );
  };

  const renderTurn = (turn, withAnnotations = false, scriptVideoId = null) => {
    // Vincent #120 (Eric 2026-05-13) — Accepter plusieurs alias pour le rôle
    // commercial. Certains scripts plus anciens ont `role: "CSF"` au lieu de
    // `role: "vendeur"`. Sans cet élargissement, les annotations « Objection
    // évitée » + « Pourquoi évitée efficacement » étaient invisibles pour ces
    // scripts (ex. "Offre alternative à carrière IA").
    const roleStr = String(turn.role || "").toLowerCase();
    const isVendor = ["vendeur", "csf", "conseiller", "vendor", "salesperson"].includes(roleStr);
    // Vincent bug #70 (2026-05-07) — afficher le RÔLE (CSF, client, mentor) au lieu
    // du nom du locuteur (Speaker 1, Jayson, etc.). On affiche le nom en petit
    // après, pour traçabilité, mais le rôle prime visuellement.
    const roleLabel = isVendor ? "CSF" : (turn.role === "client" ? "client" : (turn.role === "mentor" ? "mentor" : (turn.role || "intervenant")));
    const tc = turn.time_code || "[??:??]";
    // Vincent #99 (Eric 2026-05-12) — Préfère `start_seconds` (aligné Whisper, précis
    // à la seconde) à parsing du time_code. Fallback sur le parsing du `time_code`.
    const seekSecs = (typeof turn.start_seconds === "number" && turn.start_seconds >= 0)
      ? turn.start_seconds
      : parseTimeCodeToSeconds(tc);
    const seekable = seekSecs !== null && seekSecs !== undefined;
    // Vincent #99 (Eric 2026-05-13) — Fallback robuste : sur la page autonome
    // `/dashboard/scripts`, `videoId` (URL param) est null. On utilise alors
    // `scriptVideoId` (passé depuis `full.video_id` du doc script lui-même).
    const targetVideoId = videoId || scriptVideoId;
    const seek = () => {
      if (!seekable) return;
      if (typeof onSeek === "function") {
        onSeek(seekSecs);
        return;
      }
      if (targetVideoId) {
        navigate(`/dashboard/videos/${targetVideoId}?t=${seekSecs}`);
      }
    };
    return (
      <div className="py-1">
        <div className="flex items-start gap-2">
          {/* Vincent #99 (2026-05-12) — Click sur timecode = seek vidéo à la seconde près */}
          <button
            type="button"
            onClick={seek}
            disabled={!seekable}
            className={`text-[10px] font-mono shrink-0 mt-0.5 tabular-nums ${
              seekable
                ? "text-zinc-400 hover:text-[#002FA7] hover:underline cursor-pointer"
                : "text-zinc-400 cursor-not-allowed"
            }`}
            title={seekable ? (lang === "fr" ? "Cliquer pour aller à ce moment de la vidéo" : "Click to jump to this video moment") : ""}
            data-testid={`script-turn-timecode-${tc}`}
          >
            {tc}
          </button>
          <span className={`text-[12px] uppercase tracking-wide ${isVendor ? "font-bold text-zinc-900" : "italic text-zinc-700"}`}
            data-testid={`turn-role-${roleLabel}`}>
            {roleLabel}:
          </span>
          {/* Vincent #99 — Le texte aussi devient cliquable pour faciliter le seek */}
          <span
            className={`text-[13px] text-zinc-800 leading-relaxed ${seekable ? "cursor-pointer hover:text-[#002FA7]" : ""}`}
            onClick={seek}
            title={seekable ? (lang === "fr" ? "Cliquer pour aller à ce moment de la vidéo" : "Click to jump to this video moment") : ""}
          >
            {turn.text}
          </span>
        </div>
        {/* Vincent bug #91 (2026-05-08) — Ne plus afficher le nom du locuteur sous chaque citation.
            Vincent #120 (2026-05-11) — Ordre v3 : (1) objection évitée, (2) pourquoi évitée efficacement.
            Le champ `justification` est reformulé en « Pourquoi évitée efficacement ». */}
        {withAnnotations && isVendor && (turn.justification || turn.objection_evitee) && (
          <ul className="ml-12 mt-1 space-y-0.5">
            {turn.objection_evitee && turn.objection_evitee !== "aucune" && (
              <li className="text-[11px] text-amber-700 italic flex items-start gap-1" data-testid="v3-objection-evitee">
                <Shield size={11} weight="bold" className="mt-0.5 shrink-0" />
                <span><span className="font-semibold">{lang === "fr" ? "Objection évitée :" : "Avoided objection:"}</span> {turn.objection_evitee}</span>
              </li>
            )}
            {turn.justification && (
              <li className="text-[11px] text-teal-700 italic flex items-start gap-1" data-testid="v3-justification">
                <Lightbulb size={11} weight="bold" className="mt-0.5 shrink-0" />
                <span><span className="font-semibold">{lang === "fr" ? "Pourquoi évitée efficacement :" : "Why effectively avoided:"}</span> {turn.justification}</span>
              </li>
            )}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div
      className={embedded ? "space-y-4" : "space-y-6 p-6 max-w-6xl mx-auto"}
      data-testid={embedded ? "scripts-embedded" : "scripts-page"}
    >
      {!embedded && (
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ChatCircleText size={28} weight="duotone" className="text-[#002FA7]" />
            {lang === "fr" ? "Scripts de vente" : "Sales Scripts"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {videoId
              ? (lang === "fr" ? "Scripts détectés dans cette vidéo — 3 versions + tableau de validation" : "Scripts detected in this video — 3 versions + validation table")
              : (lang === "fr" ? "Tous les scripts de vente extraits de votre base de formations" : "All sales scripts extracted from your training base")}
          </p>
        </div>
        {videoId && isAdmin && (
          <div className="flex items-center gap-2">
            <a
              href={`${API}/api/videos/${videoId}/scripts/export.docx`}
              download
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold"
              data-testid="export-all-scripts-docx-btn"
            >
              📄 {lang === "fr" ? "Exporter (Word)" : "Export (Word)"}
            </a>
            <Button
              onClick={generateForVideo}
              disabled={generating}
              className="bg-[#002FA7] hover:bg-[#001F7A] text-white"
              data-testid="generate-scripts-btn"
            >
              <Sparkle size={16} weight="bold" className="mr-2" />
              {generating
                ? (lang === "fr" ? "Pipeline en cours..." : "Pipeline running...")
                : (lang === "fr" ? "Générer les scripts" : "Generate scripts")}
            </Button>
          </div>
        )}
      </div>
      )}

      {embedded && videoId && isAdmin && (
        <div className="flex items-center gap-2 justify-end" data-testid="scripts-embedded-actions">
          <a
            href={`${API}/api/videos/${videoId}/scripts/export.docx`}
            download
            className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold"
            data-testid="export-all-scripts-docx-btn"
          >
            📄 {lang === "fr" ? "Exporter (Word)" : "Export (Word)"}
          </a>
          <Button
            onClick={generateForVideo}
            disabled={generating}
            className="bg-[#002FA7] hover:bg-[#001F7A] text-white h-8 text-xs"
            data-testid="generate-scripts-btn"
          >
            <Sparkle size={14} weight="bold" className="mr-1.5" />
            {generating
              ? (lang === "fr" ? "Pipeline..." : "Running...")
              : (lang === "fr" ? "Générer scripts" : "Generate scripts")}
          </Button>
        </div>
      )}

      {progress && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="scripts-progress-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-blue-900">
              {progress.phase === "starting" && (lang === "fr" ? "Préparation du transcript..." : "Preparing transcript...")}
              {progress.phase === "detecting" && (lang === "fr" ? "Détection des scripts de vente (GPT-5.2)..." : "Detecting sales scripts (GPT-5.2)...")}
              {progress.phase === "detected" && (
                <span>
                  {progress.total === 0
                    ? (lang === "fr" ? "Aucun script détecté" : "No script detected")
                    : (lang === "fr"
                        ? `${progress.total} script(s) détecté(s) — génération en cours...`
                        : `${progress.total} script(s) detected — generating...`)}
                </span>
              )}
              {progress.phase === "generating" && (
                <span>
                  {lang === "fr"
                    ? `Génération script ${progress.current}/${progress.total} : ${progress.currentTitle}`
                    : `Generating script ${progress.current}/${progress.total}: ${progress.currentTitle}`}
                </span>
              )}
              {progress.phase === "complete" && (
                <span>
                  ✅ {lang === "fr"
                    ? `Terminé — ${progress.created} créé(s), ${progress.skipped} ignoré(s)`
                    : `Done — ${progress.created} created, ${progress.skipped} skipped`}
                </span>
              )}
            </div>
            <div className="text-xs text-blue-700">
              {progress.created > 0 && `✓ ${progress.created}`}
              {progress.skipped > 0 && ` ⊘ ${progress.skipped}`}
            </div>
          </div>
          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-500 ease-out"
              style={{
                width:
                  progress.phase === "complete" ? "100%" :
                  progress.phase === "generating" && progress.total > 0
                    ? `${Math.min(100, (progress.current / progress.total) * 100)}%`
                    : progress.phase === "detected" ? "20%" :
                      progress.phase === "detecting" ? "10%" : "5%",
              }}
            />
          </div>
          {progress.candidates && progress.candidates.length > 0 && progress.phase !== "complete" && (
            <div className="mt-2 text-xs text-blue-700">
              <span className="font-semibold">{lang === "fr" ? "File d'attente : " : "Queue: "}</span>
              {progress.candidates.map((c, i) => (
                <span key={`cand-${c.id || c.title || i}`} className={i < (progress.current || 0) ? "line-through opacity-50 mr-2" : "mr-2"}>
                  {c.title}{i < progress.candidates.length - 1 ? " ·" : ""}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scripts Vague 2 (Eric 2026-02-11) — Toggle Actifs / Corbeille.
          Visible pour les éditeurs uniquement. */}
      {isAdminRaw && (
        <div className="flex items-center gap-2" data-testid="scripts-viewmode-toggle">
          <button
            onClick={() => setViewMode("active")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
              viewMode === "active"
                ? "bg-[#002FA7] border-[#002FA7] text-white"
                : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            }`}
            data-testid="scripts-viewmode-active"
          >
            📋 {lang === "fr" ? "Actifs" : "Active"} ({scripts.length})
          </button>
          <button
            onClick={() => setViewMode("trash")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors inline-flex items-center gap-1.5 ${
              viewMode === "trash"
                ? "bg-red-600 border-red-600 text-white"
                : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            }`}
            data-testid="scripts-viewmode-trash"
          >
            <Trash size={12} weight="bold" /> {lang === "fr" ? "Corbeille" : "Trash"}
            {trashScripts.length > 0 && (
              <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                viewMode === "trash" ? "bg-white/20" : "bg-red-100 text-red-700"
              }`}>
                {trashScripts.length}
              </span>
            )}
          </button>
        </div>
      )}

      {viewMode === "trash" ? (
        <div data-testid="scripts-trash-view">
          {trashLoading ? (
            <div className="text-center py-16 text-muted-foreground">
              {lang === "fr" ? "Chargement…" : "Loading…"}
            </div>
          ) : trashScripts.length === 0 ? (
            <div className="text-center py-16 bg-white border border-zinc-200 rounded-xl" data-testid="trash-empty">
              <Trash size={40} weight="duotone" className="mx-auto mb-3 text-zinc-300" />
              <p className="font-semibold text-zinc-700">
                {lang === "fr" ? "Corbeille vide." : "Trash is empty."}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {lang === "fr"
                  ? "Les scripts supprimés apparaîtront ici. Rétention illimitée."
                  : "Deleted scripts will appear here. Unlimited retention."}
              </p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="trash-list">
              {trashScripts.map((s) => (
                <li
                  key={s.id}
                  className="bg-white border border-red-200 rounded-lg px-4 py-3 flex items-start justify-between gap-3"
                  data-testid={`trash-item-${s.id.slice(0, 8)}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-zinc-800" data-testid={`trash-title-${s.id.slice(0, 8)}`}>{s.title}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      <span className="text-red-600">🗑 {lang === "fr" ? "Supprimé" : "Deleted"}</span>
                      {" "}
                      {s.deleted_at && new Date(s.deleted_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}
                      {s.deleted_by_name && <> {lang === "fr" ? "par" : "by"} <strong>{s.deleted_by_name}</strong></>}
                      {s.video_title && <><span className="mx-1.5">·</span>{s.video_title}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => restoreScript(s.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
                      data-testid={`restore-script-${s.id.slice(0, 8)}`}
                      title={lang === "fr" ? "Restaurer" : "Restore"}
                    >
                      ↺ {lang === "fr" ? "Restaurer" : "Restore"}
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => permanentDeleteScript(s.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                        data-testid={`permanent-delete-${s.id.slice(0, 8)}`}
                        title={lang === "fr" ? "Supprimer définitivement" : "Delete permanently"}
                      >
                        <Trash size={11} /> {lang === "fr" ? "Suppr. déf." : "Delete forever"}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : loading ? (
        <div className="text-center py-16 text-muted-foreground">
          {lang === "fr" ? "Chargement..." : "Loading..."}
        </div>
      ) : scripts.length === 0 ? (
        <div className="text-center py-16 bg-white border border-zinc-200 rounded-xl">
          <FilmStrip size={48} weight="duotone" className="mx-auto mb-3 text-zinc-300" />
          <p className="font-semibold text-zinc-700">
            {lang === "fr" ? "Aucun script détecté pour le moment." : "No scripts detected yet."}
          </p>
          {/* Vincent B838 (Eric 2026-02-14 SPEC STRICK PROD) — Sous-message
              CTA masqué pour les non-éditeurs (le bouton « Générer les
              scripts » ne leur est de toute façon pas visible). */}
          {isAdmin && (
            <p className="text-xs text-zinc-500 mt-1">
              {videoId
                ? (lang === "fr" ? "Cliquez sur 'Générer les scripts' pour analyser la transcription." : "Click 'Generate scripts' to analyze the transcript.")
                : (lang === "fr" ? "Ouvrez une vidéo et lancez la détection depuis sa fiche." : "Open a video and run detection from its detail page.")}
            </p>
          )}
        </div>
      ) : (
        <>
        {/* Vincent XLSX 2026-07-16 Row 498 (Eric 2026-07-16 SPEC STRICK) —
            Panneau de filtres : par vidéo source + par statut de révision.
            Masqué si on est déjà sur une page vidéo-spécifique (videoId dans l'URL). */}
        {!videoId && scripts.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-lg" data-testid="scripts-filters-panel">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-zinc-700">
                {lang === "fr" ? "Vidéo :" : "Video:"}
              </label>
              <select
                value={filterVideoId}
                onChange={(e) => setFilterVideoId(e.target.value)}
                className="text-xs border border-zinc-300 rounded px-2 py-1 bg-white h-7 min-w-[220px]"
                data-testid="scripts-filter-video"
              >
                <option value="all">{lang === "fr" ? "— Toutes les vidéos —" : "— All videos —"}</option>
                {(() => {
                  const seen = new Map();
                  scripts.forEach((s) => {
                    const vid = s.video_id;
                    const vtitle = s.video_title || vid || "(sans titre)";
                    if (vid && !seen.has(vid)) seen.set(vid, vtitle);
                  });
                  return [...seen.entries()]
                    .sort((a, b) => String(a[1]).localeCompare(String(b[1]), "fr", { sensitivity: "base" }))
                    .map(([vid, vtitle]) => (
                      <option key={vid} value={vid}>{vtitle}</option>
                    ));
                })()}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-zinc-700">
                {lang === "fr" ? "Statut :" : "Status:"}
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="text-xs border border-zinc-300 rounded px-2 py-1 bg-white h-7"
                data-testid="scripts-filter-status"
              >
                <option value="all">{lang === "fr" ? "Tous" : "All"}</option>
                <option value="pending_review">{lang === "fr" ? "En révision" : "Pending review"}</option>
                <option value="approved">{lang === "fr" ? "Approuvés" : "Approved"}</option>
                <option value="rejected">{lang === "fr" ? "Rejetés" : "Rejected"}</option>
              </select>
            </div>
            {(filterVideoId !== "all" || filterStatus !== "all") && (
              <button
                onClick={() => { setFilterVideoId("all"); setFilterStatus("all"); }}
                className="text-xs px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-100"
                data-testid="scripts-filter-reset"
              >
                {lang === "fr" ? "Réinitialiser" : "Reset"}
              </button>
            )}
            <span className="text-xs text-zinc-500 ml-auto" data-testid="scripts-filter-count">
              {(() => {
                const filtered = scripts.filter((s) =>
                  (filterVideoId === "all" || s.video_id === filterVideoId) &&
                  (filterStatus === "all" || (s.status || "pending_review") === filterStatus)
                );
                return lang === "fr"
                  ? `${filtered.length} / ${scripts.length} script(s)`
                  : `${filtered.length} / ${scripts.length} script(s)`;
              })()}
            </span>
          </div>
        )}
        {/* Vincent B801 (Eric 2026-02-12) — Bouton bulk regenerate validation.
            Visible seulement en mode embarqué (dans une page vidéo) et si
            au moins un script existe.
            Vincent B868 v2 (Eric 2026-02-14) — AJOUT `isAdmin` : masquer
            les 2 boutons globaux (« Refaire validation EN LOT » et
            « Forcer tout régénérer ») pour les non-éditeurs, en cohérence
            avec le masquage du tableau de validation par script. */}
        {embedded && scripts.length > 0 && isAdmin && (
          <div className="flex justify-end gap-2 mb-2" data-testid="bulk-regen-validation-container">
            <button
              type="button"
              onClick={() => bulkRegenerateValidation()}
              disabled={bulkRegenBusy}
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 disabled:opacity-50"
              data-testid="bulk-regen-validation-btn"
              title={lang === "fr" ? "Refait la validation de tous les scripts de cette vidéo dont le tableau OU les justifications V3 sont vides ou incomplets" : "Redo validation for all scripts with empty/incomplete tables OR V3 justifications"}
            >
              {bulkRegenBusy
                ? (lang === "fr" ? "⏳ En cours…" : "⏳ Working…")
                : (lang === "fr" ? "🔄 Refaire validation EN LOT (tous les scripts)" : "🔄 Bulk redo validation (all scripts)")}
            </button>
            {/* Vincent B812 (Eric 2026-02-13) — Bouton FORCE pour régénérer
                tous les scripts même ceux considérés OK. Utile quand
                l'auto-detect ne targete rien mais Vincent veut quand
                même refaire. */}
            <button
              type="button"
              onClick={() => bulkRegenerateValidation({ force: true })}
              disabled={bulkRegenBusy}
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border border-red-300 bg-red-50 hover:bg-red-100 text-red-800 disabled:opacity-50"
              data-testid="bulk-regen-validation-force-btn"
              title={lang === "fr" ? "FORCE la régénération de TOUS les scripts, même ceux considérés complets. Utile après changement de config LLM." : "FORCE regenerate ALL scripts, even those considered complete."}
            >
              {bulkRegenBusy
                ? "⏳"
                : (lang === "fr" ? "⚡ Forcer tout régénérer" : "⚡ Force regen all")}
            </button>
          </div>
        )}
        <ul className="space-y-3" data-testid="scripts-list">
          {scripts
            .filter((s) =>
              (filterVideoId === "all" || s.video_id === filterVideoId) &&
              (filterStatus === "all" || (s.status || "pending_review") === filterStatus)
            )
            .map((s) => {
            const open = openIds[s.id];
            const full = fullScripts[s.id];
            const v3IsOpen = v3Open[s.id]; // default false
            return (
              <li
                key={s.id}
                className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
                data-testid={`script-card-${s.id.slice(0, 8)}`}
                data-focus-target={s.id}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => expand(s.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); expand(s.id); } }}
                  className="w-full px-4 py-3 flex flex-col gap-2 hover:bg-zinc-50 text-left cursor-pointer"
                >
                  {/* Vincent B730 (Eric 2026-02-13) — Nouvelle organisation
                      de la tuile en 4 lignes distinctes :
                        Ligne 1 : titre + date de révision + ✓ vert (si approuvé)
                        Ligne 2 : nom du fichier concerné (video_title)
                        Ligne 3 : horodatage + participants (vendor ↔ client + formation_date + source)
                        Ligne 4 : boutons d'action
                      Avant : tout empilé sur 2 lignes → info difficile à scanner. */}
                  <div className="flex items-start gap-2 w-full">
                    <button
                      type="button"
                      onClick={(e) => toggleFavorite(e, s.id)}
                      className={`mt-0.5 p-0.5 rounded transition-colors flex-shrink-0 ${favoriteIds.has(s.id) ? "text-rose-500" : "text-zinc-300 hover:text-rose-400"}`}
                      data-testid={`fav-btn-${s.id.slice(0, 8)}`}
                      title={favoriteIds.has(s.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                    >
                      <Heart size={18} weight={favoriteIds.has(s.id) ? "fill" : "regular"} />
                    </button>
                    {open ? <CaretDown size={18} className="mt-1 shrink-0" /> : <CaretRight size={18} className="mt-1 shrink-0" />}
                    <div className="flex-1 min-w-0">
                    {/* Ligne 1 : titre + date de révision + ✓ vert */}
                    <div className="flex items-center gap-2 flex-wrap">
                    {editingScriptId === s.id ? (
                      <input
                        value={scriptTitleDraft}
                        onChange={(e) => setScriptTitleDraft(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="flex-1 min-w-0 text-sm font-semibold px-2 py-1 border border-blue-300 rounded"
                        autoFocus
                        data-testid={`edit-script-title-input-${s.id.slice(0, 8)}`}
                      />
                    ) : (
                      <p className="font-semibold text-sm break-words" data-testid={`script-title-${s.id.slice(0, 8)}`}>{s.title}</p>
                    )}
                    {(() => {
                      const revisedIso = s.reviewed_at || s.updated_at || s.timecodes_realigned_at || s.created_at;
                      if (!revisedIso) return null;
                      const d = new Date(revisedIso);
                      if (Number.isNaN(d.getTime())) return null;
                      const label = d.toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
                      return (
                        <span
                          className="text-[11px] text-zinc-500 font-medium shrink-0"
                          data-testid={`script-revision-date-${s.id.slice(0, 8)}`}
                          title={lang === "fr" ? "Date de dernière révision" : "Last revision date"}
                        >
                          {label}
                        </span>
                      );
                    })()}
                    {s.status === "approved" && (
                      <span
                        className="inline-flex items-center gap-0.5 text-emerald-600 shrink-0"
                        data-testid={`script-approved-check-${s.id.slice(0, 8)}`}
                        title={lang === "fr" ? "Script validé" : "Script validated"}
                      >
                        <CheckCircle size={16} weight="fill" />
                      </span>
                    )}
                    </div>
                    {/* Ligne 2 : nom du fichier concerné */}
                    {s.video_title && (
                      <p className="text-[11px] text-zinc-600 mt-1 truncate" data-testid={`script-video-title-${s.id.slice(0, 8)}`}>
                        {videoId ? (
                          <span className="inline-flex items-center gap-1"><FileText size={11} className="inline shrink-0" />{s.video_title}</span>
                        ) : (
                          <Link
                            to={`/dashboard/video/${s.video_id}`}
                            className="inline-flex items-center gap-1 text-[#002FA7] hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FileText size={11} className="inline shrink-0" />{s.video_title}
                          </Link>
                        )}
                      </p>
                    )}
                    {/* Ligne 3 : horodatage + participants + formation_date + source */}
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      <span className="font-mono">{s.start_time}</span>
                      <span className="mx-1.5">·</span>
                      <span className="font-bold">{s.vendor_name}</span> ↔ <span className="italic">{s.client_name}</span>
                      {s.formation_date && <><span className="mx-1.5">·</span><ClockCounterClockwise size={10} className="inline mr-0.5" />{s.formation_date}</>}
                      {/* Vincent bug #57 (2026-05-07) — afficher la source de transcription utilisée */}
                      {s.transcript_source && (
                        <>
                          <span className="mx-1.5">·</span>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium" data-testid={`script-source-${s.id.slice(0, 8)}`}>
                            📝 {libelleSource(s.transcript_source, lang)}
                          </span>
                        </>
                      )}
                      {/* Vincent B808/B809 (Eric 2026-02-13) — Badge « Régénération
                          manuelle requise » quand le pipeline a dû tomber en
                          fallback stub. Causes possibles (dans l'ordre) :
                            1. BYOK OpenAI de l'org épuisée (crédits à recharger)
                            2. Clé Emergent (fallback interne) épuisée
                            3. Timeout ou erreur temporaire GPT
                          Cliquer sur « Refaire validation » relance la génération
                          avec un fallback BYOK→Emergent automatique. */}
                      {s.needs_manual_regeneration && (
                        <>
                          <span className="mx-1.5">·</span>
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold ring-1 ring-amber-300"
                            data-testid={`script-needs-regen-${s.id.slice(0, 8)}`}
                            title={
                              lang === "fr"
                                ? `Ce script a été construit en mode fallback : la génération IA a échoué (crédits BYOK OpenAI épuisés, quota Emergent atteint, ou timeout temporaire). Clique sur « Refaire validation » — le système essaie ta BYOK d'abord, puis bascule sur Emergent si ton org est éligible. Raisons : ${(s.regeneration_reasons || []).join(", ") || "inconnues"}`
                                : `This script was built in fallback mode: AI generation failed (BYOK OpenAI credits exhausted, Emergent quota hit, or temporary timeout). Click "Redo validation" — the system tries your BYOK first, then falls back to Emergent if your org is eligible.`
                            }
                          >
                            ⚠️ {lang === "fr" ? "Régénération manuelle requise" : "Manual regen needed"}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  </div>
                  {/* Vincent #387 (Eric 2026-06-29 SPEC STRICK) — Ligne des boutons SOUS le titre.
                      Vincent 2026-06-01 — Badge statut DÉPLACÉ vers la droite (après les
                      boutons d'action) pour cohérence avec Q&R et Procédures. Le badge à
                      gauche a été retiré (cf. ScriptsPage ligne ~635 ci-dessous). */}
                  <div className="flex flex-wrap items-center gap-1.5 pl-7" data-testid={`script-actions-${s.id.slice(0, 8)}`}>
                  {isAdmin && editingScriptId === s.id && (
                    <>
                      {/* Vincent bug #79 (2026-05-07) — bouton génération automatique
                          de titre via GPT-5.2. Affiche 3 propositions cliquables
                          + l'historique des titres antérieurs (rollback). */}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (titleProposalsLoading === s.id) return;
                          setTitleProposalsLoading(s.id);
                          try {
                            const { data } = await axios.post(`${API}/api/scripts/${s.id}/regenerate-title`, {}, { withCredentials: true });
                            setTitleProposals({ scriptId: s.id, proposals: data.proposals || [], history: data.history || [] });
                            // Vincent #213 (Eric 2026-06-04) — Erreur IA explicite
                            if ((!data.proposals || data.proposals.length === 0) && data?.error) {
                              toast.error(`${lang === "fr" ? "Échec IA" : "AI failed"} : ${data.error}`);
                            } else if (!data.proposals || data.proposals.length === 0) {
                              toast.error(lang === "fr" ? "Aucune proposition générée — vérifiez Forfait AI" : "No titles generated");
                            }
                          } catch (err) {
                            toast.error(`Erreur : ${err.response?.data?.detail || err.message}`);
                          }
                          setTitleProposalsLoading(null);
                        }}
                        disabled={titleProposalsLoading === s.id}
                        className="p-1.5 hover:bg-purple-50 text-purple-600 rounded disabled:opacity-50"
                        data-testid={`regen-title-${s.id.slice(0, 8)}`}
                        title={lang === "fr" ? "Générer 3 propositions de titre via IA" : "Generate 3 title proposals via AI"}
                      >
                        {titleProposalsLoading === s.id ? "⏳" : "✨"}
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!scriptTitleDraft.trim() || scriptTitleDraft === s.title) {
                            setEditingScriptId(null);
                            return;
                          }
                          try {
                            await axios.patch(`${API}/api/scripts/${s.id}`, { title: scriptTitleDraft.trim() }, { withCredentials: true });
                            toast.success(lang === "fr" ? "Titre mis à jour" : "Title updated");
                            setEditingScriptId(null);
                            setTitleProposals(null);
                            await load();
                          } catch (err) { toast.error(`Erreur : ${err.response?.data?.detail || err.message}`); }
                        }}
                        className="p-1.5 hover:bg-teal-50 text-teal-600 rounded inline-flex items-center"
                        data-testid={`save-script-title-${s.id.slice(0, 8)}`}
                        title={lang === "fr" ? "Sauvegarder" : "Save"}
                      ><FloppyDisk size={14} weight="bold" /></button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingScriptId(null); setTitleProposals(null); }}
                        className="p-1.5 hover:bg-zinc-100 text-zinc-500 rounded inline-flex items-center"
                        data-testid={`cancel-script-title-${s.id.slice(0, 8)}`}
                      ><X size={14} weight="bold" /></button>
                    </>
                  )}
                  {isAdmin && editingScriptId !== s.id && (
                    <>
                      {/* Vincent #369 (Eric 2026-06-22 SPEC STRICK) — Boutons avec TEXTE
                          (au lieu d'icônes seules) pour parité avec les autres tuiles
                          de connaissance : "Titre auto IA", "Éditer", "Accepter",
                          "Refuser", "Historique", "Effacer". */}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (titleProposalsLoading === s.id) return;
                          setTitleProposalsLoading(s.id);
                          setEditingScriptId(s.id);
                          setScriptTitleDraft(s.title || "");
                          try {
                            const { data } = await axios.post(`${API}/api/scripts/${s.id}/regenerate-title`, {}, { withCredentials: true });
                            setTitleProposals({ scriptId: s.id, proposals: data.proposals || [], history: data.history || [] });
                          } catch (err) {
                            toast.error(`Erreur : ${err.response?.data?.detail || err.message}`);
                          }
                          setTitleProposalsLoading(null);
                        }}
                        disabled={titleProposalsLoading === s.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50"
                        data-testid={`quick-regen-title-${s.id.slice(0, 8)}`}
                        title={lang === "fr" ? "Générer 3 titres IA et entrer en édition" : "Generate 3 AI titles and enter edit mode"}
                      >
                        {titleProposalsLoading === s.id ? "⏳" : "✨"} {lang === "fr" ? "Titre auto IA" : "AI title"}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingScriptId(s.id);
                          setScriptTitleDraft(s.title || "");
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                        data-testid={`edit-script-title-${s.id.slice(0, 8)}`}
                        title={lang === "fr" ? "Éditer le titre" : "Edit title"}
                      >
                        <Pencil size={11} weight="bold" /> {lang === "fr" ? "Éditer" : "Edit"}
                      </button>
                    </>
                  )}
                  {/* Vincent #369 (Eric 2026-06-22 SPEC STRICK) — boutons TEXTE :
                      Accepter / Refuser TOUJOURS disponibles (même après validation,
                      on peut revenir en arrière). Historique toujours visible. */}
                  {isAdmin && editingScriptId !== s.id && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const targetStatus = s.status === "approved" ? "pending" : "approved";
                        const label = targetStatus === "approved"
                          ? (lang === "fr" ? "Accepter ce script ?" : "Accept this script?")
                          : (lang === "fr" ? "Retirer l'approbation (repasser en attente) ?" : "Revoke approval (set back to pending)?");
                        const ok = await confirmDialog(label, {
                          confirmLabel: targetStatus === "approved"
                            ? (lang === "fr" ? "Accepter" : "Accept")
                            : (lang === "fr" ? "Retirer" : "Revoke"),
                        });
                        if (!ok) return;
                        try {
                          await axios.patch(`${API}/api/scripts/${s.id}`, { status: targetStatus }, { withCredentials: true });
                          toast.success(targetStatus === "approved"
                            ? (lang === "fr" ? "Script accepté" : "Script accepted")
                            : (lang === "fr" ? "Approbation retirée" : "Approval revoked"));
                          await load();
                        } catch (err) { toast.error(`Erreur : ${err.response?.data?.detail || err.message}`); }
                      }}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border ${
                        s.status === "approved"
                          ? "border-teal-300 bg-teal-100 text-teal-800 hover:bg-teal-200"
                          : "border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100"
                      }`}
                      data-testid={`accept-script-${s.id.slice(0, 8)}`}
                      title={s.status === "approved"
                        ? (lang === "fr" ? "Retirer l'approbation" : "Revoke approval")
                        : (lang === "fr" ? "Accepter ce script" : "Accept this script")}
                    >
                      <Check size={11} weight="bold" /> {lang === "fr" ? "Accepter" : "Accept"}
                      {s.status === "approved" && " ✓"}
                    </button>
                  )}
                  {isAdmin && editingScriptId !== s.id && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const targetStatus = s.status === "rejected" ? "pending" : "rejected";
                        const label = targetStatus === "rejected"
                          ? (lang === "fr" ? "Refuser ce script ?" : "Reject this script?")
                          : (lang === "fr" ? "Retirer le rejet (repasser en attente) ?" : "Revoke rejection (set back to pending)?");
                        const ok = await confirmDialog(label, {
                          danger: targetStatus === "rejected",
                          confirmLabel: targetStatus === "rejected"
                            ? (lang === "fr" ? "Refuser" : "Reject")
                            : (lang === "fr" ? "Retirer" : "Revoke"),
                        });
                        if (!ok) return;
                        try {
                          await axios.patch(`${API}/api/scripts/${s.id}`, { status: targetStatus }, { withCredentials: true });
                          toast.success(targetStatus === "rejected"
                            ? (lang === "fr" ? "Script refusé" : "Script rejected")
                            : (lang === "fr" ? "Rejet retiré" : "Rejection revoked"));
                          await load();
                        } catch (err) { toast.error(`Erreur : ${err.response?.data?.detail || err.message}`); }
                      }}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border ${
                        s.status === "rejected"
                          ? "border-rose-300 bg-rose-100 text-rose-800 hover:bg-rose-200"
                          : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                      }`}
                      data-testid={`reject-script-${s.id.slice(0, 8)}`}
                      title={s.status === "rejected"
                        ? (lang === "fr" ? "Retirer le rejet" : "Revoke rejection")
                        : (lang === "fr" ? "Refuser ce script" : "Reject this script")}
                    >
                      <X size={11} weight="bold" /> {lang === "fr" ? "Refuser" : "Reject"}
                      {s.status === "rejected" && " ✓"}
                    </button>
                  )}
                  {isAdmin && editingScriptId !== s.id && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const { data } = await axios.get(`${API}/api/scripts/${s.id}/history`, { withCredentials: true });
                          setHistoryEntry({ script: s, history: data.history || [] });
                        } catch (err) {
                          setHistoryEntry({ script: s, history: s.edit_history || [] });
                        }
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                      data-testid={`script-history-${s.id.slice(0, 8)}`}
                      title={lang === "fr" ? "Historique des modifications" : "Edit history"}
                    >
                      <Clock size={11} weight="bold" /> {lang === "fr" ? "Historique" : "History"}
                    </button>
                  )}
                  {/* Vincent B803 (Eric 2026-02-13) — Bouton « Refaire validation »
                      individuel, visible au niveau de la barre d'actions du script
                      (parité avec le bouton bulk). Avant ce fix, il n'était visible
                      que dans le <details> de la table de validation, enterré 2
                      clics plus loin. */}
                  {isAdmin && editingScriptId !== s.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); regenerateValidation(s.id); }}
                      disabled={regeneratingValidationFor === s.id}
                      className={
                        s.needs_manual_regeneration
                          ? "inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold rounded border-2 border-amber-500 bg-amber-100 text-amber-900 hover:bg-amber-200 disabled:opacity-50 animate-pulse"
                          : "inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                      }
                      data-testid={`quick-regen-validation-${s.id.slice(0, 8)}`}
                      title={
                        s.needs_manual_regeneration
                          ? (lang === "fr"
                              ? "⚠️ REQUIS — Ce script a été construit en mode fallback. Clique pour régénérer avec ta BYOK OpenAI (fallback Emergent auto si éligible)."
                              : "⚠️ REQUIRED — This script was built in fallback mode. Click to regenerate with your BYOK OpenAI (auto Emergent fallback if eligible).")
                          : (lang === "fr"
                              ? "Refaire la validation de cohérence de CE script uniquement"
                              : "Redo coherence validation for THIS script only")
                      }
                    >
                      {regeneratingValidationFor === s.id
                        ? (lang === "fr" ? "⏳ …" : "⏳ …")
                        : (lang === "fr" ? "🔄 Refaire validation" : "🔄 Redo validation")}
                    </button>
                  )}
                  {/* Vincent #380 (Eric 2026-06-23 SPEC STRICK) — Bouton "Word" RETIRÉ :
                      Vincent ne veut plus permettre le téléchargement d'un script de
                      vente (confidentialité/sécurité). On laisse uniquement Effacer. */}
                  {isAdmin && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteScript(s.id); }}
                      disabled={deletingId === s.id}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                      data-testid={`delete-script-${s.id.slice(0, 8)}`}
                      title={lang === "fr" ? "Effacer ce script" : "Delete this script"}
                    >
                      <Trash size={11} /> {lang === "fr" ? "Effacer" : "Delete"}
                    </button>
                  )}
                  {/* Vincent 2026-06-01 — Badge statut DROITE (cohérence Q&R/Procédures :
                      crayon · ✓ · corbeille · badge). Remplace l'ancien badge gauche. */}
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded inline-flex items-center font-bold ${
                      s.status === "approved"
                        ? "bg-teal-100 text-teal-700"
                        : s.status === "rejected"
                          ? "bg-red-100 text-red-700"
                          : "bg-amber-100 text-amber-700"
                    }`}
                    title={
                      s.status === "approved" ? (lang === "fr" ? "Validé" : "Validated")
                      : s.status === "rejected" ? (lang === "fr" ? "Refusé" : "Rejected")
                      : (lang === "fr" ? "En attente de validation" : "Pending validation")
                    }
                    data-testid={`script-status-${s.id.slice(0, 8)}`}
                  >
                    {s.status === "approved" ? <Check size={12} weight="bold" /> : s.status === "rejected" ? "✕" : "..."}
                  </span>
                  </div>
                </div>

                {/* Vincent bug #79 (2026-05-07) — Propositions de titre + historique.
                    Affiché juste sous la barre quand l'admin a cliqué le bouton ✨. */}
                {editingScriptId === s.id && titleProposals && titleProposals.scriptId === s.id && (
                  <div className="border-t border-zinc-200 bg-purple-50/40 px-4 py-3" data-testid={`title-proposals-${s.id.slice(0,8)}`}>
                    {titleProposals.proposals.length > 0 && (
                      <div className="mb-3">
                        <div className="text-[11px] font-semibold text-purple-800 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                          ✨ {lang === "fr" ? "Propositions IA — cliquez pour adopter" : "AI proposals — click to adopt"}
                        </div>
                        <div className="space-y-1">
                          {titleProposals.proposals.map((prop, pi) => (
                            <button
                              key={pi}
                              onClick={(e) => { e.stopPropagation(); setScriptTitleDraft(prop); }}
                              className={`w-full text-left text-xs px-2 py-1.5 rounded border transition-all ${
                                scriptTitleDraft === prop
                                  ? "bg-teal-100 border-teal-400 text-teal-900 font-semibold"
                                  : "bg-white border-zinc-200 text-zinc-700 hover:border-purple-300 hover:bg-purple-50"
                              }`}
                              data-testid={`title-proposal-${s.id.slice(0,8)}-${pi}`}
                            >
                              {scriptTitleDraft === prop && "✓ "}{prop}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {titleProposals.history.length > 0 && (
                      <div>
                        <div className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                          🕐 {lang === "fr" ? "Historique (rollback)" : "History (rollback)"}
                        </div>
                        <div className="space-y-1">
                          {titleProposals.history.slice().reverse().map((h, hi) => (
                            <button
                              key={hi}
                              onClick={(e) => { e.stopPropagation(); setScriptTitleDraft(h.title); }}
                              className="w-full text-left text-[11px] px-2 py-1 rounded border border-zinc-200 bg-white hover:border-blue-300 text-zinc-600 italic"
                              title={`Remplacé le ${new Date(h.replaced_at).toLocaleString("fr-CA")} par ${h.replaced_by_email || "?"}`}
                              data-testid={`title-history-${s.id.slice(0,8)}-${hi}`}
                            >
                              ↩ {h.title}
                              <span className="text-[9px] text-zinc-400 ml-2">{new Date(h.replaced_at).toLocaleDateString("fr-CA")}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {open && full && (
                  <div className="px-4 pb-4 border-t border-zinc-100 bg-zinc-50/50">
                    {/* Header recap */}
                    <div className="text-[11px] text-zinc-600 py-2.5 font-mono flex flex-wrap items-center gap-2">
                      <span>[CSF] = <span className="font-bold not-italic">{full.vendor_name}</span></span>
                      <span>·</span>
                      <span>[client] = <span className="italic">{full.client_name}</span></span>
                      {/* Vincent 2026-05-06 (bug #57, #63) — Indique la source de la transcription */}
                      {full.transcript_source && (
                        <>
                          <span>·</span>
                          <span className="text-purple-600 not-italic font-sans">
                            📄 {lang === "fr" ? "Source : transcription" : "Source: transcript"} <strong>{libelleSource(full.transcript_source, lang)}</strong>
                          </span>
                        </>
                      )}
                    </div>

                    {/* V1 — Original verbatim — Vincent 2026-05-07 bug #80: triangle accordéon */}
                    <details className="mb-3 bg-white rounded-lg border border-zinc-200 group" data-testid={`v1-${s.id.slice(0, 8)}`}>
                      <summary className="px-3 py-2 cursor-pointer text-[12px] font-semibold text-zinc-700 hover:bg-zinc-50 flex items-center gap-2 list-none">
                        <span className="text-zinc-400 transition-transform group-open:rotate-90 inline-block w-3">▶</span>
                        <span className="inline-block px-1.5 py-0.5 bg-zinc-200 text-zinc-700 rounded text-[10px] font-bold">V1</span>
                        {lang === "fr" ? "Originale (verbatim)" : "Original (verbatim)"}
                      </summary>
                      <ul className="px-4 py-2 space-y-1 list-disc list-outside text-[12px]">
                        {(full.v1_original || []).map((t, i) => (
                          <li key={`v1-${s.id}-${t.time_code || i}`}>{renderTurn(t, false, full.video_id)}</li>
                        ))}
                      </ul>
                    </details>

                    {/* V2 — Reformulée (open by default per Vincent spec) */}
                    <details open className="mb-3 bg-white rounded-lg border border-teal-200 group" data-testid={`v2-${s.id.slice(0, 8)}`}>
                      <summary className="px-3 py-2 cursor-pointer text-[12px] font-semibold text-teal-700 hover:bg-teal-50 flex items-center gap-2 list-none">
                        <span className="text-teal-500 transition-transform group-open:rotate-90 inline-block w-3">▶</span>
                        <span className="inline-block px-1.5 py-0.5 bg-teal-200 text-teal-800 rounded text-[10px] font-bold">V2</span>
                        {lang === "fr" ? "Reformulée professionnelle (tutoiement)" : "Professional rewrite (tu)"}
                        {/* Vincent (Eric 2026-05-18) — Bouton Éditer V2 : passe le bloc en
                            mode édition tour-par-tour. Le rôle et le time_code restent
                            verrouillés (alignement Whisper) ; seul le texte est modifiable. */}
                        {isAdmin && !(editingContent?.scriptId === s.id && editingContent?.version === "v2") && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEditContent(full, "v2"); }}
                            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] rounded border border-teal-300 bg-white hover:bg-teal-100 text-teal-700 font-medium"
                            data-testid={`edit-v2-content-${s.id.slice(0, 8)}`}
                            title={lang === "fr" ? "Éditer le contenu V2 (texte des tours de parole)" : "Edit V2 content (turn texts)"}
                          >
                            <Pencil size={11} weight="bold" /> {lang === "fr" ? "Éditer V2" : "Edit V2"}
                          </button>
                        )}
                      </summary>
                      {editingContent?.scriptId === s.id && editingContent?.version === "v2" ? (
                        <div className="px-4 py-3 space-y-2" data-testid={`edit-v2-pane-${s.id.slice(0, 8)}`}>
                          <p className="text-[10.5px] text-teal-700 italic">
                            {lang === "fr"
                              ? "Modifiez le texte de chaque tour de parole. Le rôle et le time-code restent fixes."
                              : "Edit the text of each turn. Role and time-code stay fixed."}
                          </p>
                          {contentDraft.map((t, i) => {
                            const roleStr = String(t.role || "").toLowerCase();
                            const isVendor = ["vendeur", "csf", "conseiller", "vendor", "salesperson"].includes(roleStr);
                            const roleLabel = isVendor ? "CSF" : (t.role === "client" ? "client" : (t.role === "mentor" ? "mentor" : (t.role || "intervenant")));
                            return (
                              <div key={`v2-edit-${s.id}-${i}`} className="flex items-start gap-2 p-2 rounded border border-teal-100 bg-teal-50/40">
                                <span className="text-[10px] font-mono text-zinc-500 mt-1.5 shrink-0 w-12">{t.time_code || ""}</span>
                                <span className={`text-[11px] uppercase tracking-wide mt-1 shrink-0 w-14 ${isVendor ? "font-bold text-zinc-900" : "italic text-zinc-700"}`}>{roleLabel}</span>
                                <textarea
                                  value={t.text || ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setContentDraft(prev => prev.map((x, xi) => xi === i ? { ...x, text: v } : x));
                                  }}
                                  rows={Math.max(2, Math.min(6, (t.text || "").split("\n").length + 1))}
                                  className="flex-1 text-[12px] px-2 py-1 border border-teal-200 rounded resize-y bg-white focus:border-teal-500 focus:ring-1 focus:ring-emerald-300 outline-none"
                                  data-testid={`edit-v2-turn-${s.id.slice(0, 8)}-${i}`}
                                />
                              </div>
                            );
                          })}
                          <div className="flex justify-end gap-2 pt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={cancelEditContent}
                              disabled={savingContent}
                              data-testid={`cancel-v2-content-${s.id.slice(0, 8)}`}
                            >
                              <X size={13} className="mr-1" /> {lang === "fr" ? "Annuler" : "Cancel"}
                            </Button>
                            <Button
                              size="sm"
                              onClick={saveEditContent}
                              disabled={savingContent}
                              className="bg-teal-600 hover:bg-teal-700 text-white"
                              data-testid={`save-v2-content-${s.id.slice(0, 8)}`}
                            >
                              <Check size={13} className="mr-1" /> {savingContent ? "…" : (lang === "fr" ? "Enregistrer" : "Save")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        (full.v2_reformule || []).length > 0 ? (
                          <ul className="px-4 py-2 space-y-1 list-disc list-outside text-[12px]">
                            {(full.v2_reformule || []).map((t, i) => (
                              <li key={`v2-${s.id}-${t.time_code || i}`}>{renderTurn(t, false, full.video_id)}</li>
                            ))}
                          </ul>
                        ) : (
                          /* Vincent #270 (2026-06-12) — État vide EXPLICITE :
                             l'accordéon s'ouvrait sur du vide et Vincent croyait à un bug. */
                          <p className="px-4 py-3 text-[11.5px] italic text-zinc-500" data-testid={`v2-empty-${s.id.slice(0, 8)}`}>
                            {lang === "fr"
                              ? "Version V2 (reformulée) non générée pour ce script. Relancez la génération des scripts depuis la page vidéo → onglet Scripts → Pipeline."
                              : "V2 (reworded) version not generated for this script. Re-run script generation from the video page → Scripts tab → Pipeline."}
                          </p>
                        )
                      )}
                    </details>

                    {/* V3 — Justifiée (collapsed by default per Vincent spec) */}
                    <details className="mb-3 bg-white rounded-lg border border-amber-200 group" data-testid={`v3-${s.id.slice(0, 8)}`} open={v3IsOpen}>
                      <summary
                        className="px-3 py-2 cursor-pointer text-[12px] font-semibold text-amber-700 hover:bg-amber-50 flex items-center gap-2 list-none"
                        onClick={(e) => { e.preventDefault(); setV3Open((p) => ({ ...p, [s.id]: !p[s.id] })); }}
                      >
                        <span className={`text-amber-500 transition-transform inline-block w-3 ${v3IsOpen ? "rotate-90" : ""}`}>▶</span>
                        <span className="inline-block px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded text-[10px] font-bold">V3</span>
                        {lang === "fr" ? "Justification / objections" : "Justification / objections"}
                        {/* Vincent B867 (Eric 2026-02-14 SPEC STRICK PROD) —
                            Bouton « Éditer V3 » désormais TOUJOURS visible
                            pour les admins (parité avec « Éditer V2 »).
                            Auparavant conditionné à `v3IsOpen`, ce qui
                            forçait Vincent à d'abord déplier V3 avant de
                            pouvoir déclencher l'édition. */}
                        {isAdmin && !(editingContent?.scriptId === s.id && editingContent?.version === "v3") && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!v3IsOpen) setV3Open(p => ({ ...p, [s.id]: true })); startEditContent(full, "v3"); }}
                            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] rounded border border-amber-300 bg-white hover:bg-amber-100 text-amber-700 font-medium"
                            data-testid={`edit-v3-content-${s.id.slice(0, 8)}`}
                            title={lang === "fr" ? "Éditer le contenu V3 (texte + objection + justification)" : "Edit V3 content (text + objection + justification)"}
                          >
                            <Pencil size={11} weight="bold" /> {lang === "fr" ? "Éditer V3" : "Edit V3"}
                          </button>
                        )}
                      </summary>
                      {v3IsOpen && (
                        editingContent?.scriptId === s.id && editingContent?.version === "v3" ? (
                          <div className="px-4 py-3 space-y-2" data-testid={`edit-v3-pane-${s.id.slice(0, 8)}`}>
                            <p className="text-[10.5px] text-amber-700 italic">
                              {/* Vincent B867 (Eric 2026-02-14 SPEC STRICK PROD) —
                                  Édition étendue : texte + objection évitée +
                                  justification (uniquement sur les tours du
                                  vendeur/CSF, cf. affichage read-only). */}
                              {lang === "fr"
                                ? "Modifiez le texte, l'objection évitée et la justification de chaque tour vendeur."
                                : "Edit text, avoided objection and justification for each seller turn."}
                            </p>
                            {contentDraft.map((t, i) => {
                              const roleStr = String(t.role || "").toLowerCase();
                              const isVendor = ["vendeur", "csf", "conseiller", "vendor", "salesperson"].includes(roleStr);
                              const roleLabel = isVendor ? "CSF" : (t.role === "client" ? "client" : (t.role === "mentor" ? "mentor" : (t.role || "intervenant")));
                              return (
                                <div key={`v3-edit-${s.id}-${i}`} className="flex flex-col gap-1.5 p-2 rounded border border-amber-100 bg-amber-50/40">
                                  <div className="flex items-start gap-2">
                                    <span className="text-[10px] font-mono text-zinc-500 mt-1.5 shrink-0 w-12">{t.time_code || ""}</span>
                                    <span className={`text-[11px] uppercase tracking-wide mt-1 shrink-0 w-14 ${isVendor ? "font-bold text-zinc-900" : "italic text-zinc-700"}`}>{roleLabel}</span>
                                    <textarea
                                      value={t.text || ""}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setContentDraft(prev => prev.map((x, xi) => xi === i ? { ...x, text: v } : x));
                                      }}
                                      rows={Math.max(2, Math.min(6, (t.text || "").split("\n").length + 1))}
                                      className="flex-1 text-[12px] px-2 py-1 border border-amber-200 rounded resize-y bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-300 outline-none"
                                      data-testid={`edit-v3-turn-${s.id.slice(0, 8)}-${i}`}
                                    />
                                  </div>
                                  {/* Vincent B867 — Champs annotations V3 (vendeur uniquement). */}
                                  {isVendor && (
                                    <div className="pl-[104px] flex flex-col gap-1">
                                      <label className="text-[10px] uppercase tracking-wide font-semibold text-amber-800">
                                        {lang === "fr" ? "Objection évitée" : "Avoided objection"}
                                      </label>
                                      <textarea
                                        value={t.objection_evitee || ""}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setContentDraft(prev => prev.map((x, xi) => xi === i ? { ...x, objection_evitee: v } : x));
                                        }}
                                        rows={2}
                                        placeholder={lang === "fr" ? "Ex : refus de payer d'avance…" : "Ex: refusal to pay upfront…"}
                                        className="text-[11.5px] px-2 py-1 border border-amber-200 rounded resize-y bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-300 outline-none"
                                        data-testid={`edit-v3-objection-${s.id.slice(0, 8)}-${i}`}
                                      />
                                      <label className="text-[10px] uppercase tracking-wide font-semibold text-teal-800 mt-1">
                                        {lang === "fr" ? "Justification (pourquoi évitée efficacement)" : "Justification (why effectively avoided)"}
                                      </label>
                                      <textarea
                                        value={t.justification || ""}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setContentDraft(prev => prev.map((x, xi) => xi === i ? { ...x, justification: v } : x));
                                        }}
                                        rows={2}
                                        placeholder={lang === "fr" ? "Ex : le CSF cadre le contexte AMF…" : "Ex: seller frames the AMF context…"}
                                        className="text-[11.5px] px-2 py-1 border border-teal-200 rounded resize-y bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-300 outline-none"
                                        data-testid={`edit-v3-justification-${s.id.slice(0, 8)}-${i}`}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <div className="flex justify-end gap-2 pt-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={cancelEditContent}
                                disabled={savingContent}
                                data-testid={`cancel-v3-content-${s.id.slice(0, 8)}`}
                              >
                                <X size={13} className="mr-1" /> {lang === "fr" ? "Annuler" : "Cancel"}
                              </Button>
                              <Button
                                size="sm"
                                onClick={saveEditContent}
                                disabled={savingContent}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid={`save-v3-content-${s.id.slice(0, 8)}`}
                              >
                                <Check size={13} className="mr-1" /> {savingContent ? "…" : (lang === "fr" ? "Enregistrer" : "Save")}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          (full.v3_justifie || []).length > 0 ? (
                            <ul className="px-4 py-2 space-y-2 list-disc list-outside text-[12px]">
                              {(full.v3_justifie || []).map((t, i) => (
                                <li key={`v3-${s.id}-${t.time_code || i}`}>{renderTurn(t, true, full.video_id)}</li>
                              ))}
                            </ul>
                          ) : (
                            /* Vincent #270 (2026-06-12) — État vide EXPLICITE (cf. V2). */
                            <p className="px-4 py-3 text-[11.5px] italic text-zinc-500" data-testid={`v3-empty-${s.id.slice(0, 8)}`}>
                              {lang === "fr"
                                ? "Version V3 (justifiée AMF) non générée pour ce script. Relancez la génération des scripts depuis la page vidéo → onglet Scripts → Pipeline."
                                : "V3 (AMF-justified) version not generated for this script. Re-run script generation from the video page → Scripts tab → Pipeline."}
                            </p>
                          )
                        )
                      )}
                    </details>

                    {/* Validation table — Vincent B794 v2 (2026-02-12) :
                        Bouton « Régénérer validation » ajouté pour rattraper
                        les vieux scripts avec table vide/malformée. Le
                        <details> s'affiche MAINTENANT même si vide (avant :
                        il était caché quand length===0).
                        Vincent B868 (Eric 2026-02-14 SPEC STRICK PROD) —
                        Tableau de validation MASQUÉ pour les utilisateurs
                        non-éditeurs (readOnly OU !isAdmin) : ils voient
                        uniquement V1/V2/V3. Seuls les concepteurs de
                        formations ont besoin de valider la cohérence. */}
                    {isAdmin && !readOnly && (
                    <details
                      className="bg-white rounded-lg border border-zinc-200 overflow-hidden"
                      data-testid={`validation-table-${s.id.slice(0, 8)}`}
                      open={(full.validation_table || []).length === 0}
                    >
                      <summary className="px-3 py-2 cursor-pointer border-b border-zinc-200 bg-zinc-50 hover:bg-zinc-100 flex items-center justify-between gap-2">
                        <span className="text-[12px] font-semibold text-zinc-700 inline-flex items-center gap-2">
                          <Check size={14} className="text-teal-600" />
                          {lang === "fr" ? "Validation de cohérence" : "Coherence validation"}
                          <span className="text-[10px] text-zinc-500">({(full.validation_table || []).length})</span>
                        </span>
                        {/* Vincent B794 v2c (Eric 2026-02-12) — Bouton
                            « Refaire validation » (renommé de
                            « Régénérer » à la demande de Vincent).
                            AFFICHÉ EN PERMANENCE (retiré du guard
                            !readOnly) : le backend vérifie déjà
                            _require_kb_editor et Vincent peut vouloir
                            le déclencher en mode Visualiser. */}
                        <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              regenerateValidation(s.id);
                            }}
                            disabled={regeneratingValidationFor === s.id}
                            className="text-[10.5px] px-2 py-0.5 rounded border border-amber-300 bg-white hover:bg-amber-50 text-amber-800 disabled:opacity-50"
                            data-testid={`regenerate-validation-btn-${s.id.slice(0, 8)}`}
                            title={lang === "fr" ? "Reconstruit le tableau de validation via GPT-5.2 (utile si vide ou mal rempli). Réservé aux éditeurs." : "Rebuild the validation table via GPT-5.2 (useful if empty or malformed). Editors only."}
                          >
                            {regeneratingValidationFor === s.id
                              ? (lang === "fr" ? "⏳ En cours…" : "⏳ Working…")
                              : (lang === "fr" ? "🔄 Refaire validation" : "🔄 Redo validation")}
                          </button>
                      </summary>
                      {(full.validation_table || []).length === 0 ? (
                        <div className="p-4 text-[11.5px] italic text-zinc-500 text-center">
                          {lang === "fr"
                            ? "Tableau vide. Clique sur « Refaire validation » pour le reconstruire à partir des versions V1/V2/V3."
                            : "Empty table. Click \"Redo validation\" to rebuild it from V1/V2/V3."}
                        </div>
                      ) : (
                      <div className="overflow-x-auto">
                          <table className="w-full text-[11px]" data-testid={`validation-table-3cols-${s.id.slice(0, 8)}`}>
                            <thead className="bg-zinc-50 text-zinc-600">
                              <tr>
                                <th className="px-2 py-1.5 text-left font-semibold w-[8%]">[HH:MM:SS]</th>
                                {/* Vincent B794 v2 (Eric 2026-02-12) — 3 colonnes en parallèle
                                    V1 / V2 / V3 comme demandé : « le tableau de validation
                                    ne devrait pas être vide, mais montrer les 3 versions du
                                    script en parallèle. » */}
                                <th className="px-2 py-1.5 text-left font-semibold w-[30%] bg-zinc-100">
                                  {lang === "fr" ? "V1 — Citation originale (verbatim)" : "V1 — Original citation (verbatim)"}
                                </th>
                                <th className="px-2 py-1.5 text-left font-semibold w-[30%] bg-blue-50">
                                  {lang === "fr" ? "V2 — Phrase reformulée" : "V2 — Reformulated phrase"}
                                </th>
                                <th className="px-2 py-1.5 text-left font-semibold w-[32%] bg-amber-50">
                                  {lang === "fr" ? "V3 — Justification (pourquoi ça marche + objection évitée)" : "V3 — Justification (why it works + objection avoided)"}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                              {full.validation_table.map((row, i) => {
                                const seekable = parseTimeCodeToSeconds(row.time_code) !== null;
                                const _hasV3 = (row.justification || "").trim().length > 0 || (row.objection_evitee || "").trim().length > 0;
                                return (
                                  <tr key={`valid-${s.id}-${i}-${row.time_code || ""}`} className="hover:bg-zinc-50/50 align-top">
                                    <td className="px-2 py-1.5 font-mono">
                                      <button
                                        type="button"
                                        onClick={() => seekable && seekFromTimeCode(row.time_code, full.video_id)}
                                        disabled={!seekable}
                                        className={seekable ? "text-blue-600 hover:underline cursor-pointer" : "text-zinc-500 cursor-default"}
                                        title={seekable ? (lang === "fr" ? "Aller à ce moment" : "Jump here") : ""}
                                        data-testid={`validation-tc-${i}`}
                                      >
                                        {row.time_code || "—"}
                                      </button>
                                    </td>
                                    <td className="px-2 py-1.5 text-zinc-700 italic bg-zinc-50/40">
                                      {row.citation_originale || <span className="text-zinc-400">—</span>}
                                    </td>
                                    <td className="px-2 py-1.5 text-zinc-800 bg-blue-50/30">
                                      {row.phrase_reformule || <span className="text-zinc-400">—</span>}
                                    </td>
                                    <td className="px-2 py-1.5 bg-amber-50/30">
                                      {_hasV3 ? (
                                        <div className="space-y-1">
                                          {row.justification && (
                                            <div className="text-teal-700 italic flex items-start gap-1">
                                              <Lightbulb size={11} weight="bold" className="mt-0.5 shrink-0" />
                                              <span><span className="font-semibold not-italic text-zinc-700">{lang === "fr" ? "Pourquoi :" : "Why:"}</span> {row.justification}</span>
                                            </div>
                                          )}
                                          {row.objection_evitee && row.objection_evitee !== "aucune" && (
                                            <div className="text-amber-700 italic flex items-start gap-1">
                                              <Shield size={11} weight="bold" className="mt-0.5 shrink-0" />
                                              <span><span className="font-semibold not-italic text-zinc-700">{lang === "fr" ? "Objection évitée :" : "Objection avoided:"}</span> {row.objection_evitee}</span>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-zinc-400">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </details>
                    )}
                    {/* Fin B868 : fermeture de la condition `isAdmin && !readOnly`. */}

                    {/* Scripts Vague 2 (Eric 2026-02-11) — Section commentaires
                        threadés, ouverte à tout utilisateur de l'org. */}
                    {renderCommentsPanel(s.id)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        </>
      )}
      {/* Vincent #362 (Eric 2026-06-19 SPEC STRICK) — Modal historique scripts */}
      {historyEntry && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setHistoryEntry(null)}
          data-testid="script-history-modal"
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Historique — {historyEntry.script?.title || "Script"}
              </h3>
              <button onClick={() => setHistoryEntry(null)} className="text-zinc-400 hover:text-zinc-700">
                <X size={20} />
              </button>
            </div>
            {historyEntry.history?.length > 0 ? (
              <ul className="space-y-3">
                {historyEntry.history.map((h, i) => (
                  <li key={h.edited_at || h.created_at || `hist-${i}`} className="border border-zinc-200 rounded p-3 text-sm">
                    <div className="flex justify-between text-xs text-zinc-500 mb-1">
                      <span>{h.edited_at || h.created_at}</span>
                      <span>{h.edited_by_email || h.author || "—"}</span>
                    </div>
                    {h.fields_changed?.length > 0 && (
                      <p className="text-xs text-zinc-600 mb-2">
                        Champs modifiés : <strong>{h.fields_changed.join(", ")}</strong>
                      </p>
                    )}
                    {h.previous_title !== h.new_title && (h.previous_title || h.new_title) && (
                      <div className="mb-1">
                        <span className="line-through text-rose-700">{h.previous_title}</span>
                        {" → "}
                        <span className="text-teal-700">{h.new_title}</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-zinc-500">Aucun historique de modification.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
